// PIN -> Supabase password transform.
//
// The shop logs in with a 4-digit PIN, but Supabase Auth enforces a 6-char
// minimum password. The hub maps the PIN to a Supabase password with this
// fixed prefix so the user only ever types the PIN. This is NOT a secret (it
// ships in the browser bundle) — it's a format adapter. Real protection is the
// PIN entropy + Supabase's auth rate limiting + RLS.
//
// MUST stay identical to the copy in scripts/migrate/set-pins.mjs.
export const pinToPassword = (pin) => `3cha-${String(pin || '').trim()}`;

// The 3 role accounts. Email is derived from the role.
export const roleEmail = (role) => `${role}@3c-detailing.com`;

// Every account a PIN might belong to, in the order DevLogin tries them.
//
// Ordered by how often each one actually signs in — each miss is a full network
// round-trip to GoTrue, so the common cases go first. (A lookup endpoint would
// collapse this to one call, but api/ is pinned at the 12-function Vercel cap;
// fold it into invoicer.js if login latency ever becomes a real complaint.)
//
// Email is NOT derivable from role any more: Albany has its own detailer, so
// `detailer` maps to two different accounts. Hence an explicit list.
export const LOGIN_ACCOUNTS = [
  { email: 'principal@3c-detailing.com' },
  { email: 'detailer@3c-detailing.com' },
  { email: 'owner@3c-detailing.com' },
  { email: 'hd@3c-detailing.com' },                 // Ric — HD at Albany, detailer at Kingston
  { email: 'detailer.albany@3c-detailing.com' },    // 3CAL shop floor
  { email: 'automotive-supervisor@3c-detailing.com' },
  { email: 'tax-advisor@3c-detailing.com' },
  { email: 'otto@3c-detailing.com' },              // OTTO dealership portal (3COT)
];

// Verify a PIN belongs to a given role WITHOUT disturbing the live session.
// Used for in-app "manager override" gates (e.g. booking a conflict). We spin up
// a throwaway Supabase client with persistSession:false + an isolated storageKey
// so signInWithPassword validates the PIN but never writes over the real session
// (the booker stays signed in as themselves). Defaults to the owner account.
export async function verifyRolePin(pin, role = 'owner') {
  const clean = String(pin || '').trim();
  if (!clean) return false;
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!url || !key) return false;
  try {
    const { createClient } = await import('@supabase/supabase-js');
    const tmp = createClient(url, key, {
      auth: { persistSession: false, autoRefreshToken: false, storageKey: '3c_pincheck_tmp' },
    });
    const { data, error } = await tmp.auth.signInWithPassword({
      email: roleEmail(role), password: pinToPassword(clean),
    });
    const ok = !error && !!(data && data.user) &&
      ((data.user.app_metadata && data.user.app_metadata.role) === role);
    try { await tmp.auth.signOut({ scope: 'local' }); } catch (_) {}
    return ok;
  } catch (_) { return false; }
}

// Like verifyRolePin, but returns the throwaway session's access token when the
// PIN matches the given role — so a money-moving server endpoint (e.g. the
// balance refund) can be gated on a real, server-verifiable owner JWT obtained
// from the PIN, without disturbing the live session. Returns the token string,
// or null if the PIN doesn't match that role.
export async function tokenFromRolePin(pin, role = 'owner') {
  const clean = String(pin || '').trim();
  if (!clean) return null;
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!url || !key) return null;
  try {
    const { createClient } = await import('@supabase/supabase-js');
    const tmp = createClient(url, key, {
      auth: { persistSession: false, autoRefreshToken: false, storageKey: '3c_pincheck_tmp' },
    });
    const { data, error } = await tmp.auth.signInWithPassword({
      email: roleEmail(role), password: pinToPassword(clean),
    });
    const ok = !error && !!(data && data.user) &&
      ((data.user.app_metadata && data.user.app_metadata.role) === role);
    const token = ok ? ((data.session && data.session.access_token) || null) : null;
    try { await tmp.auth.signOut({ scope: 'local' }); } catch (_) {}
    return token;
  } catch (_) { return null; }
}
